import { FC, MouseEvent, ReactNode, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge, TooltipCard } from '@components';
import { TenantProfileExtractionStatus } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  DotsThreeIcon,
  FileDocIcon,
  FilePdfIcon,
  FileZipIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react';

import type { TenantProfileDocumentDto } from '../../api/types';

import { deriveDisplayStatus, type HubDisplayStatus } from './documentStatusMeta';
import { DocumentStatusPill } from './DocumentStatusPill';
import { formatFileSize, formatUploadedDateTime } from './hubFormat';

interface Props {
  doc: TenantProfileDocumentDto;
  /** Localized category name resolved from the active template. */
  categoryName: string | null;
  onReview: (id: string) => void;
  onExtract: (id: string) => void;
  onRetryClassify: (id: string) => void;
  onDelete: (id: string) => void;
}

/** Max characters of the filename to show before truncating with an ellipsis. */
const MAX_NAME_CHARS = 32;

/** Truncate a filename to {@link MAX_NAME_CHARS} characters (full name shown on hover). */
function truncateName(fileName: string): string {
  return fileName.length > MAX_NAME_CHARS
    ? `${fileName.slice(0, MAX_NAME_CHARS)}…`
    : fileName;
}

/** Pick a file-type glyph + tint from the filename extension. */
function fileGlyph(fileName: string) {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return { Icon: FilePdfIcon, color: 'error.main' as const };
  if (ext === 'zip') return { Icon: FileZipIcon, color: 'text.tertiary' as const };
  return { Icon: FileDocIcon, color: 'blue.700' as const };
}

interface PrimaryAction {
  labelKey: SingularKeys;
  run: () => void;
  variant: 'contained' | 'secondary' | 'tertiary';
  icon?: ReactNode;
}

export const DocumentTableRow: FC<Props> = observer(
  ({ doc, categoryName, onReview, onExtract, onRetryClassify, onDelete }) => {
    const { t, i18n } = useTrans();
    const theme = useTheme();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const status = deriveDisplayStatus(doc);
    const { Icon, color } = fileGlyph(doc.fileName);
    const pages = doc.classificationMetadata.pageCount;
    const isPublished = status === 'done';

    const openMenu = (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setMenuAnchor(e.currentTarget);
    };
    const closeMenu = () => setMenuAnchor(null);

    // A FAILED document can have failed at classification OR extraction. The
    // retry must target the stage that actually failed: a failed extraction
    // (classification already valid) re-runs extraction, while a failed
    // classification re-runs classification.
    const extractionFailed =
      doc.extractionMetadata.status === TenantProfileExtractionStatus.FAILED;

    const primary = primaryAction(status, doc.id, extractionFailed, {
      onReview,
      onExtract,
      onRetryClassify,
      onDelete,
    });

    return (
      <TableRow
        sx={{
          '& > td': {
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
            py: 2.25,
          },
          '& > td:first-of-type': { pl: 2 },
          '& > td:last-of-type': { pr: 3 },
          '&:hover': { backgroundColor: theme.palette.grey[200] },
          transition: 'background-color .12s',
        }}
      >
        {/* Document */}
        <TableCell sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.200',
                color,
              }}
            >
              <Icon size={18} weight="fill" />
            </Box>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Typography
                variant="textSM_semiBold"
                color="text.primary"
                title={doc.fileName}
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {truncateName(doc.fileName)}
              </Typography>
              <Typography
                variant="textXS_regular"
                color="text.tertiary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatFileSize(doc.fileSize)}
                {pages ? ` · ${t('app.documentHub.meta.pages', { count: pages })}` : ''}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {/* Category */}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Badge
              variant="DomainName"
              label={categoryName ?? t('app.documentHub.table.uncategorized')}
              // Allow a long domain name to ellipsize inside the cell instead of
              // overflowing into the Status column.
              sx={{
                minWidth: 0,
                maxWidth: '100%',
                flexShrink: 1,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          </Box>
        </TableCell>

        {/* Status */}
        <TableCell>
          <DocumentStatusPill status={status} />
        </TableCell>

        {/* Progress / Detail */}
        <TableCell sx={{ maxWidth: 240 }}>
          <ProgressDetail doc={doc} status={status} />
        </TableCell>

        {/* Uploaded */}
        <TableCell>
          <UploadedAt iso={doc.uploadedAt} locale={i18n.language} />
        </TableCell>

        {/* Actions */}
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            {primary && (
              <Button
                size="small"
                variant={primary.variant}
                startIcon={primary.icon}
                onClick={() => primary.run()}
                sx={{
                  textTransform: 'none',
                  flex: 'none',
                  // Match the app's secondary button: a white surface needs a
                  // border to read as clickable; the theme variant supplies the
                  // grey hover.
                  ...(primary.variant === 'secondary' && {
                    border: 1,
                    borderColor: 'grey.300',
                  }),
                }}
              >
                {t(primary.labelKey)}
              </Button>
            )}
            <IconButton size="small" onClick={openMenu} aria-label={t('app.documentHub.table.actions')}>
              <DotsThreeIcon size={18} color={theme.palette.text.tertiary} />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    borderRadius: 2,
                    boxShadow: 4,
                    border: 1,
                    borderColor: 'grey.200',
                  },
                },
              }}
            >
              <TooltipCard
                variant="dark"
                description={isPublished ? t('app.documentHub.action.deletePublished') : ''}
                placement="left"
              >
                <span>
                  <MenuItem
                    disabled={isPublished}
                    onClick={() => {
                      closeMenu();
                      onDelete(doc.id);
                    }}
                    sx={{ typography: 'textSM_regular', gap: 1, color: 'error.main' }}
                  >
                    <TrashIcon size={16} />
                    {t('app.documentHub.action.delete')}
                  </MenuItem>
                </span>
              </TooltipCard>
            </Menu>
          </Box>
        </TableCell>
      </TableRow>
    );
  },
);

function primaryAction(
  status: HubDisplayStatus,
  id: string,
  extractionFailed: boolean,
  handlers: {
    onReview: (id: string) => void;
    onExtract: (id: string) => void;
    onRetryClassify: (id: string) => void;
    onDelete: (id: string) => void;
  },
): PrimaryAction | null {
  switch (status) {
    case 'pending_review':
      return {
        labelKey: 'app.documentHub.action.review',
        run: () => handlers.onReview(id),
        variant: 'contained',
      };
    case 'done':
      return {
        labelKey: 'app.documentHub.action.view',
        run: () => handlers.onReview(id),
        variant: 'secondary',
      };
    case 'ready_to_extract':
      return {
        labelKey: 'app.documentHub.action.extract',
        run: () => handlers.onExtract(id),
        variant: 'contained',
      };
    case 'irrelevant':
      // "Remove" must delete the irrelevant document (soft-delete), not
      // re-classify it — re-classifying just resets it to PENDING and the doc
      // reappears instead of being removed.
      return {
        labelKey: 'app.documentHub.action.remove',
        run: () => handlers.onDelete(id),
        variant: 'secondary',
      };
    case 'failed':
      // Re-run the stage that failed: extraction failures hit the extract
      // endpoint; classification failures hit the (re)classify endpoint.
      return {
        labelKey: 'app.documentHub.action.retry',
        run: () =>
          extractionFailed ? handlers.onExtract(id) : handlers.onRetryClassify(id),
        variant: 'secondary',
      };
    case 'classifying':
    case 'extracting':
      // In flight — the only available control is to cancel (delete) the doc.
      return {
        labelKey: 'app.documentHub.action.cancel',
        run: () => handlers.onDelete(id),
        variant: 'tertiary',
        icon: <XIcon size={14} />,
      };
    default:
      return null;
  }
}

/** A thin colored progress bar matching the design's track under each detail. */
const Bar: FC<{ color: string; value?: number }> = observer(({ color, value }) => (
  <LinearProgress
    variant={value == null ? 'indeterminate' : 'determinate'}
    value={value}
    sx={{
      height: 4,
      borderRadius: 3,
      backgroundColor: 'grey.200',
      '& .MuiLinearProgress-bar': { backgroundColor: color },
    }}
  />
));

/** The "Progress / Detail" column content, by display status. */
const ProgressDetail: FC<{
  doc: TenantProfileDocumentDto;
  status: HubDisplayStatus;
}> = observer(({ doc, status }) => {
  const { t } = useTrans();
  const theme = useTheme();
  const extracted = doc.extractionMetadata.extractedFieldCount;

  if (status === 'extracting' || status === 'classifying') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="textXS_regular" color="blue.700">
          {status === 'classifying'
            ? t('app.documentHub.detail.classifying')
            : t('app.documentHub.status.extracting')}
        </Typography>
        <Bar color={theme.palette.blue[700]} />
      </Box>
    );
  }

  if (status === 'ready_to_extract') {
    return (
      <Typography variant="textXS_regular" color="text.tertiary">
        {t('app.documentHub.detail.readyToExtract')}
      </Typography>
    );
  }

  if (status === 'failed' || status === 'irrelevant') {
    const reason =
      doc.extractionMetadata.lastError ??
      doc.classificationMetadata.reasonText ??
      doc.classificationMetadata.lastError ??
      null;
    if (!reason) return <Dash />;
    return (
      <Typography
        variant="textXS_regular"
        color={status === 'failed' ? 'error.main' : 'text.tertiary'}
        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
      >
        {reason}
      </Typography>
    );
  }

  // done / pending_review — extraction finished; show a filled bar + field tally.
  const done = status === 'done';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="textXS_regular" color={done ? 'green.700' : 'yellow.800'}>
        {extracted != null
          ? `${t('app.documentHub.detail.fields', { filled: extracted, total: extracted })}${done ? ' ✓' : ''}`
          : done
            ? t('app.documentHub.status.done')
            : t('app.documentHub.status.pendingReview')}
      </Typography>
      <Bar
        color={done ? theme.palette.green[700] : theme.palette.yellow[800]}
        value={100}
      />
    </Box>
  );
});

const Dash: FC = observer(() => (
  <Typography variant="textXS_regular" color="text.disabled">
    —
  </Typography>
));

/** Two-line absolute upload timestamp: date on top, time beneath. */
const UploadedAt: FC<{ iso: string; locale: string }> = observer(({ iso, locale }) => {
  const { date, time } = formatUploadedDateTime(iso, locale);
  if (!date) return <Dash />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="textSM_regular" color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
        {date}
      </Typography>
      <Typography variant="textXS_regular" color="text.tertiary" sx={{ whiteSpace: 'nowrap' }}>
        {time}
      </Typography>
    </Box>
  );
});
